import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function TopbarPopover({
  label,
  menuLabel,
  buttonClassName,
  panelClassName,
  children,
  panel,
}: {
  label: string;
  menuLabel?: string;
  buttonClassName: string;
  panelClassName: string;
  children: ReactNode;
  panel: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    const timer = window.setTimeout(() => document.addEventListener("pointerdown", onPointerDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="topbar-popover" ref={rootRef}>
      <button
        type="button"
        className={buttonClassName}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        {children}
      </button>
      {open ? (
        <div id={panelId} className={panelClassName} role="menu" aria-label={menuLabel ?? label}>
          {panel(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
